// TSIA2 test preview, opened from the Tests tab by clicking a test's name.
//
// The house preview (TestSATPreview) reading the TSIA2 preview endpoint, so a
// tutor moves through this form the way they move through every other one:
// module dropdown, Directions, Show answer / Explanation, the question
// navigator, Print. What is TSIA2's own is shown where the SAT page shows its
// equivalents -- the stage and its cutoff in the module dropdown (where the
// SAT page says Hard/Easy), each item's instruction line above the stem, and
// which calculator the student is offered beside the question number.
//
// There is no attempt behind a preview, so both stages of both sections are
// listed regardless of routing.
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import {
    MdClose,
    MdExitToApp,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
    MdOutlineCalculate,
    MdOutlineLocationOn,
    MdOutlinePrint,
} from 'react-icons/md';
import { useReactToPrint } from 'react-to-print';

import Button from 'components/Button2';
import Header from 'components/Header';
import Loader from 'components/Loader';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import { applyKatexWrap, flattenKatexUnderlines } from 'global/katexWrap';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import './index.css';

import 'katex/dist/katex.min.css';
import renderMathInElement from 'katex/dist/contrib/auto-render.js';

// Loaded from the CDN in public/index.html, as the other previews use it.
declare const Desmos: any;

const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D'];
const DISABLE_PRINT = ['ReadingTown', 'College Planning Corp', 'A+ Program', 'MT Learning Center', 'PREPARE Test Prep', 'High Peak Education', 'WellEd Labs'];

const CALCULATOR_LABELS: Record<string, string> = {
    basic: 'Basic calculator',
    sqrt: 'Square root calculator',
    graphing: 'Graphing calculator',
};

type PreviewChoice = {
    id: number;
    label: string | null;
    choice: string;
    figure: string | null;
    isCorrect: boolean;
};

type PreviewQuestion = {
    id: number;
    questionId: string;
    questionNum: number;
    instructions: string | null;
    calculatorMode: 'basic' | 'sqrt' | 'graphing' | null;
    question: string;
    passages: { id: string; introduction: string | null; passage: string }[];
    figures: { id: string; image: string | null }[];
    choices: PreviewChoice[];
    correctChoice: number | null;
    explanation: string | null;
    explanationFigure: string | null;
    explanationVideo: string | null;
};

type PreviewModule = {
    name: string;
    subject: string | null;
    sectionName: string;
    moduleNumber: number;
    stage: 'CRC' | 'Diagnostic';
    cutoff: number | null;
    prompt: string | null;
    questions: PreviewQuestion[];
};

// What the SAT page prints as "(Hard)" / "(Easy)": the stage, and for the
// CRC the score below which the Diagnostic is served.
function stageLabel(testModule: PreviewModule) {
    if (testModule.stage === 'CRC' && testModule.cutoff !== null) {
        return `(cutoff ${testModule.cutoff}/${testModule.questions.length})`;
    }
    return '';
}

function directionsFor(testModule: PreviewModule) {
    if (testModule.prompt) return testModule.prompt;
    // The Diagnostic carries no welcome text on the real test, because
    // arriving at it must not announce itself. Tell the tutor instead.
    const section = testModule.subject ?? testModule.sectionName;
    return `This Diagnostic stage is served only to a student who scores below the cutoff on the ${section} CRC. The student is not told which stage they are on.`;
}

function TestTSIA2Preview() {
    const [testName, setTestName] = useState<string | null>(null);
    const [testModules, setTestModules] = useState<PreviewModule[] | null>(null);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [moduleSelectOpen, setModuleSelectOpen] = useState(false);
    const [directionsOpen, setDirectionsOpen] = useState(false);
    const [navigatorOpen, setNavigatorOpen] = useState(false);
    const [isPrint, setIsPrint] = useState(false);
    const [calculator, setCalculator] = useState<any>(null);
    const [calculatorOpen, setCalculatorOpen] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);
    const [showAnswer, setShowAnswer] = useState(false);
    // Per-(org, test) clean-print allowlist flag from the preview endpoint.
    const [cleanPrintTest, setCleanPrintTest] = useState(false);

    const { user } = useAuthContext();
    // Clean version applies when the org-wide customization is set OR this specific
    // (org, test) pair is on the per-test clean-print allowlist.
    const cleanVersionEnabled = cleanPrintTest || !!user.customizations.find(customization => customization === 'Test Clean Version Print');
    const printRef = useRef(null);
    const navigate = useNavigate();
    const { testId } = useParams();
    const moduleSelectRef = useOutsideClickHandler(() => setTimeout(() => setModuleSelectOpen(false), 10));
    const directionsRef = useOutsideClickHandler(() => setTimeout(() => setDirectionsOpen(false), 10));
    const navigatorRef = useOutsideClickHandler(() => setTimeout(() => setNavigatorOpen(false), 10));

    const prevQuestion = () => {
        if (!testModules) return;
        if (questionIndex > 0) {
            setQuestionIndex(questionIndex - 1);
        }
        else if (moduleIndex > 0) {
            setQuestionIndex(testModules[moduleIndex - 1].questions.length - 1);
            setModuleIndex(moduleIndex - 1);
        }
    };

    const nextQuestion = () => {
        if (!testModules) return;
        if (questionIndex < testModules[moduleIndex].questions.length - 1) {
            setQuestionIndex(questionIndex + 1);
        }
        else if (moduleIndex < testModules.length - 1) {
            setModuleIndex(moduleIndex + 1);
            setQuestionIndex(0);
        }
        else {
            navigate('/tests');
        }
    };

    const goToQuestion = (index: number) => {
        setQuestionIndex(index);

        const passages = document.getElementById('testTSIA2Preview__passages');
        const question = document.getElementById('testTSIA2Preview__questionChoices');

        if (passages) passages.scrollTop = 0;
        if (question) question.scrollTop = 0;
    };

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    { left: '$$', right: '$$', display: true },
                    { left: '$', right: '$', display: false },
                    { left: '\\(', right: '\\)', display: false },
                    { left: '\\[', right: '\\]', display: true },
                ],
                trust: true,
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        // Long ELAR passages must wrap inside the pane rather than scroll it
        // sideways; the same helper the TSIA2 review uses.
        applyKatexWrap(document, { enabled: true });

        // A wrapped KaTeX underline paints its lines on top of each other;
        // the shared helper flattens it to text and keeps any italic.
        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    };

    const dragMove = (element: HTMLElement, header: HTMLElement) => {
        header.style.cursor = 'grab';
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;

        const closeDragElement = () => {
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        };

        const dragElement = (event) => {
            event.preventDefault();

            if (event.type === 'mousemove') {
                pos1 = pos3 - event.clientX;
                pos2 = pos4 - event.clientY;
                pos3 = event.clientX;
                pos4 = event.clientY;
            } else if (event.type === 'touchmove') {
                pos1 = pos3 - event.touches[0].clientX;
                pos2 = pos4 - event.touches[0].clientY;
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
            }

            if (pos3 > 0 && pos4 > 0 && pos3 < window.innerWidth && pos4 < window.innerHeight) {
                element.style.top = (element.offsetTop - pos2) + 'px';
                element.style.left = (element.offsetLeft - pos1) + 'px';
            }
            else {
                closeDragElement();
            }
        };

        const dragMouseDown = (event) => {
            event.preventDefault();

            if (event.type === 'mousedown') {
                pos3 = event.clientX;
                pos4 = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = closeDragElement;
            } else if (event.type === 'touchstart') {
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = closeDragElement;
            }
        };

        const startsOnClose = (event: Event) => (
            event.target instanceof Element && event.target.closest('.testTSIA2Preview__popupHeaderIcon')
        );

        header.onmousedown = (event) => {
            if (startsOnClose(event)) return;
            dragMouseDown(event);
        };

        header.ontouchstart = (event) => {
            if (startsOnClose(event)) return;
            dragMouseDown(event);
        };
    };

    const printLog = () => {
        axios.post('tutors/tests/printLog/', {
            testId,
            cleanVersion: (!showAnswer && cleanVersionEnabled) ? true : false,
        })
            .catch(error => {
                console.error(error);
            });
    };

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        onAfterPrint: () => {
            setIsPrint(false);
            printLog();
        },
    });

    const onPrint = () => {
        setIsPrint(true);
    };

    useEffect(() => {
        if (isPrint) {
            renderKatex();
            handlePrint();
        }
    }, [isPrint]);

    useEffect(() => {
        axios.get(`tests/digital/tsia2/preview/${testId}/`)
            .then(res => {
                setTestName(res.data.name);
                setTestModules(res.data.testModules);
                setCleanPrintTest(res.data.cleanPrintTest);
            })
            .catch(error => {
                console.error(error);
                navigate('/tests');
            });
    }, [testId]);

    useEffect(() => {
        if (!testModules) return undefined;
        let instance: any = null;
        const calculatorEl = document.getElementById('testTSIA2Preview__calculator');
        if (calculatorEl && typeof Desmos !== 'undefined') {
            instance = Desmos.GraphingCalculator(calculatorEl, {
                notes: false,
                folders: false,
                images: false,
                links: false,
            });
            setCalculator(instance);
        }

        const pairs: [string, string][] = [
            ['testTSIA2Preview__explanationDrag', 'testTSIA2Preview__explanationHeader'],
            ['testTSIA2Preview__calculatorDrag', 'testTSIA2Preview__calculatorHeader'],
        ];
        for (const [dragId, headerId] of pairs) {
            const dragEl = document.getElementById(dragId);
            const headerEl = document.getElementById(headerId);
            if (dragEl && headerEl) {
                dragMove(dragEl, headerEl);
            }
        }

        // Destroy the Desmos instance when the modules change or the page
        // unmounts; otherwise each visit leaks one and a refetch stacks a
        // second calculator in the same box.
        return () => {
            instance?.destroy();
            setCalculator(null);
        };
    }, [testModules]);

    useLayoutEffect(() => {
        if (testModules) {
            renderKatex();
        }
    }, [testModules, moduleIndex, questionIndex]);

    useEffect(() => {
        if (testModules) {
            renderKatex();
        }
    }, [directionsOpen, showExplanation]);

    useEffect(() => {
        if (calculatorOpen && calculator) {
            calculator.focusFirstExpression();
        }
    }, [calculatorOpen]);

    if (testModules === null) return <Loader />;

    const testModule = testModules[moduleIndex];
    const { name, questions } = testModule;
    const question = questions[questionIndex] as PreviewQuestion | undefined;
    const isMath = (testModule.subject ?? '').includes('Math');
    if (!question) {
        return (
            <>
            <Header title={testName} prevRoutes={[{ name: 'All Tests', path: '/tests' }]} />
            <div className='testTSIA2Preview__box'>
                <div className='testTSIA2Preview'>
                    <p className='testTSIA2Preview__questionQuestion'>
                        {name} has no questions to preview.
                    </p>
                </div>
            </div>
            </>
        );
    }
    const canPrint = !(
        user.organizations.some(organization => DISABLE_PRINT.includes(organization))
        || user.customizations.find(customization => customization === 'Disable Print')
    );

    const renderChoices = (item: PreviewQuestion, reveal: boolean) => (
        item.choices.map((choice, i) => (
            <div key={choice.id} className='testTSIA2Preview__questionChoiceWrapper'>
                <button
                    className={reveal && choice.isCorrect
                        ? 'testTSIA2Preview__questionChoice--correct'
                        : 'testTSIA2Preview__questionChoice'
                    }
                    onClick={() => {}}
                >
                    <div className='testTSIA2Preview__questionChoiceOutline'></div>
                    <p className='testTSIA2Preview__questionChoiceLetter'>
                        {choice.label ?? MULTIPLE_CHOICES[i]}
                    </p>
                    {choice.figure
                        ? <img
                            className='testTSIA2Preview__questionChoiceFigure'
                            src={choice.figure}
                            alt={choice.label ?? MULTIPLE_CHOICES[i]}
                            draggable={false}
                        />
                        : <p className='testTSIA2Preview__questionChoiceText'>
                            <span className='katex'>
                                {choice.choice}
                            </span>
                        </p>
                    }
                </button>
            </div>
        ))
    );

    const renderFigures = (item: PreviewQuestion, alt: string) => (
        item.figures.map(figure => (
            figure.image && (
                <img
                    className='testTSIA2Preview__figure'
                    key={figure.id}
                    src={figure.image}
                    alt={alt}
                    draggable={false}
                />
            )
        ))
    );

    return (
        <>
        <Helmet>
            <title>
                Test Preview - Test Center Admin
            </title>
        </Helmet>
        <Header title={testName} prevRoutes={[{ name: 'All Tests', path: '/tests' }]} />
        <div className='testTSIA2Preview__box' onCopy={(event) => { event.preventDefault(); }}>
            {/* Rendered only while printing: every stage of every section. */}
            {isPrint &&
                <div className='testTSIA2Preview__print' ref={printRef}>
                    {testModules.map((printModule, m) => (
                        <React.Fragment key={`${printModule.sectionName}-${printModule.moduleNumber}`}>
                            <h2 className={m > 0
                                ? 'testTSIA2Preview__title testTSIA2Preview__pageBreakBefore'
                                : 'testTSIA2Preview__title'
                            }>
                                {printModule.name} {stageLabel(printModule)}
                            </h2>
                            {printModule.questions.map((printQuestion, q) => {
                                const reveal = showAnswer || !cleanVersionEnabled;
                                return (
                                    <React.Fragment key={printQuestion.id}>
                                        <div className='testTSIA2Preview__test testTSIA2Preview__pageBreak'>
                                            {printQuestion.passages.length > 0 && <>
                                                <div className='testTSIA2Preview__material'>
                                                    <div className='testTSIA2Preview__materialContent'>
                                                        <div className='testTSIA2Preview__passages'>
                                                            {renderFigures(printQuestion, (q + 1).toString())}
                                                            {printQuestion.passages.map(({ id, passage }) => (
                                                                <div key={id} className='testTSIA2Preview__passage'>
                                                                    <p className='katex testTSIA2Preview__passageText'>
                                                                        {passage}
                                                                    </p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='testTSIA2Preview__verticalLine'></div>
                                            </>}
                                            <div className='testTSIA2Preview__question'>
                                                <div className='testTSIA2Preview__questionContent'>
                                                    <div className='testTSIA2Preview__questionHeader'>
                                                        <p className='testTSIA2Preview__questionNumber'>
                                                            {q + 1}
                                                        </p>
                                                    </div>
                                                    <div className='testTSIA2Preview__questionChoices'>
                                                        {printQuestion.passages.length === 0 && renderFigures(printQuestion, (q + 1).toString())}
                                                        {printQuestion.instructions &&
                                                            <p className='testTSIA2Preview__instructions'>
                                                                {printQuestion.instructions}
                                                            </p>
                                                        }
                                                        <p className='testTSIA2Preview__questionQuestion'>
                                                            <span className='katex'>
                                                                {printQuestion.question}
                                                            </span>
                                                        </p>
                                                        {renderChoices(printQuestion, reveal)}
                                                        <div className='testTSIA2Preview__marginBottom'></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {reveal && <>
                                            <div>Explanation: </div>
                                            {printQuestion.explanationFigure && (
                                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                                    <img
                                                        src={printQuestion.explanationFigure}
                                                        alt='Explanation Figure'
                                                        style={{ maxWidth: printQuestion.explanation ? '70%' : '100%', height: 'auto' }}
                                                    />
                                                </div>
                                            )}
                                            {printQuestion.explanation &&
                                                <div className='testTSIA2Preview__printExpl'>
                                                    <span className='katex'>{printQuestion.explanation}</span>
                                                </div>
                                            }
                                        </>}
                                    </React.Fragment>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </div>
            }

            <div className='testTSIA2Preview'>
                {/* test header */}
                <div className='testTSIA2Preview__header'>
                    <div className='testTSIA2Preview__titleGroup'>
                        <button className='testTSIA2Preview__moduleSelect' onClick={() => setModuleSelectOpen(!moduleSelectOpen)}>
                            <h1 className='testTSIA2Preview__title'>
                                {name}
                            </h1>
                            {moduleSelectOpen
                                ? <MdKeyboardArrowUp className='testTSIA2Preview__moduleSelectIcon' />
                                : <MdKeyboardArrowDown className='testTSIA2Preview__moduleSelectIcon' />
                            }
                            {moduleSelectOpen &&
                                <div className='testTSIA2Preview__moduleSelectDropdown' ref={moduleSelectRef}>
                                    {testModules.map((option, t) => (
                                        <p
                                            key={`${option.sectionName}-${option.moduleNumber}`}
                                            className={t === moduleIndex
                                                ? 'testTSIA2Preview__moduleSelectDropdownItem--selected'
                                                : 'testTSIA2Preview__moduleSelectDropdownItem'
                                            }
                                            onClick={() => {
                                                if (t !== moduleIndex) {
                                                    setModuleIndex(t);
                                                    setQuestionIndex(0);
                                                }
                                            }}
                                        >
                                            {option.name} {stageLabel(option)}
                                        </p>
                                    ))}
                                </div>
                            }
                        </button>
                        <button className='testTSIA2Preview__directionsButton' onClick={() => setDirectionsOpen(!directionsOpen)}>
                            {directionsOpen
                                ? <u>Directions</u>
                                : <p>Directions</p>
                            }
                            {directionsOpen
                                ? <MdKeyboardArrowUp className='testTSIA2Preview__directionsIcon' />
                                : <MdKeyboardArrowDown className='testTSIA2Preview__directionsIcon' />
                            }
                        </button>
                    </div>
                    {directionsOpen &&
                        <div className='testTSIA2Preview__directions' ref={directionsRef}>
                            <div className='testTSIA2Preview__directionsBubble'>
                                <p className='katex testTSIA2Preview__directionsText'>
                                    {directionsFor(testModule)}
                                </p>
                                <Button
                                    className='testTSIA2Preview__directionsClose'
                                    onClick={() => setDirectionsOpen(false)}
                                    secondary
                                >
                                    CLOSE
                                </Button>
                            </div>
                        </div>
                    }

                    {/* header actions */}
                    <div className='testTSIA2Preview__actionGroup'>
                        {canPrint &&
                            <button
                                className='testTSIA2Preview__action'
                                onClick={onPrint}
                            >
                                <MdOutlinePrint className='testTSIA2Preview__actionIcon' />
                                <p className='testTSIA2Preview__actionText'>Print</p>
                            </button>
                        }
                        {isMath &&
                            <button
                                className={calculatorOpen
                                    ? 'testTSIA2Preview__action--active'
                                    : 'testTSIA2Preview__action'
                                }
                                onClick={() => setCalculatorOpen(!calculatorOpen)}
                            >
                                <MdOutlineCalculate className='testTSIA2Preview__actionIcon' />
                                <p className='testTSIA2Preview__actionText'>Calculator</p>
                            </button>
                        }
                        <button
                            className='testTSIA2Preview__action'
                            onClick={() => navigate(-1)}
                        >
                            <MdExitToApp className='testTSIA2Preview__actionIcon' />
                            <p className='testTSIA2Preview__actionText'>Exit</p>
                        </button>
                    </div>
                    <div id='testTSIA2Preview__explanationDrag' className={showExplanation
                        ? 'testTSIA2Preview__popup--explanation'
                        : 'testTSIA2Preview__popup--hidden'
                    }>
                        <div id='testTSIA2Preview__explanationHeader' className='testTSIA2Preview__popupHeader'>
                            <h3 className='testTSIA2Preview__popupHeaderText'>Explanation</h3>
                            <MdClose className='testTSIA2Preview__popupHeaderIcon' onClick={() => setShowExplanation(false)} />
                        </div>
                        <div className='testTSIA2Preview__explanationContainer'>
                            {question.explanationFigure && (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <img
                                        src={question.explanationFigure}
                                        alt='Explanation Figure'
                                        style={{ maxWidth: question.explanation ? '80%' : '100%', height: 'auto' }}
                                    />
                                </div>
                            )}
                            <div className='testTSIA2Preview__explanationBox katex'>
                                {question.explanation
                                    ? question.explanation
                                    : question.explanationFigure
                                        ? ''
                                        : 'No explanation'
                                }
                            </div>
                        </div>
                    </div>
                    <div id='testTSIA2Preview__calculatorDrag' className={calculatorOpen
                        ? 'testTSIA2Preview__popup--calculator'
                        : 'testTSIA2Preview__popup--hidden'
                    }>
                        <div id='testTSIA2Preview__calculatorHeader' className='testTSIA2Preview__popupHeader'>
                            <h3 className='testTSIA2Preview__popupHeaderText'>Calculator</h3>
                            <MdClose className='testTSIA2Preview__popupHeaderIcon' onClick={() => setCalculatorOpen(false)} />
                        </div>
                        <div
                            id='testTSIA2Preview__calculator'
                            style={{ width: 400, height: 600 }}
                        ></div>
                    </div>
                </div>
                <div className='testTSIA2Preview__dottedLine'></div>

                {/* test content */}
                <div className='testTSIA2Preview__test'>
                    {/* left: passage */}
                    {question.passages.length > 0 && <>
                        <div className='testTSIA2Preview__material'>
                            <div className='testTSIA2Preview__materialContent'>
                                <div id='testTSIA2Preview__passages' className='testTSIA2Preview__passages'>
                                    {renderFigures(question, (questionIndex + 1).toString())}
                                    {question.passages.map(({ id, passage }) => (
                                        <div key={id} className='testTSIA2Preview__passage'>
                                            <p
                                                id={id}
                                                className='katex testTSIA2Preview__passageText'
                                            >
                                                {passage}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className='testTSIA2Preview__verticalLine'></div>
                    </>}

                    {/* right: question */}
                    <div className='testTSIA2Preview__question'>
                        <div className='testTSIA2Preview__questionContent'>
                            <div className='testTSIA2Preview__questionHeader'>
                                <p className='testTSIA2Preview__questionNumber'>
                                    {questionIndex + 1}
                                </p>
                                {/* Which calculator the student is offered on
                                    this item; the real test varies it per item. */}
                                {question.calculatorMode &&
                                    <p className='testTSIA2Preview__calculatorTag'>
                                        <MdOutlineCalculate className='testTSIA2Preview__calculatorTagIcon' />
                                        {CALCULATOR_LABELS[question.calculatorMode]}
                                    </p>
                                }
                                <div className='testTSIA2Preview__questionButtons'>
                                    {(question.explanation || question.explanationFigure) &&
                                        <button
                                            className={showExplanation
                                                ? 'testTSIA2Preview__explanation--active'
                                                : 'testTSIA2Preview__explanation'
                                            }
                                            onClick={() => setShowExplanation(!showExplanation)}
                                        >
                                            Explanation
                                        </button>
                                    }
                                    <button
                                        className={showAnswer
                                            ? 'testTSIA2Preview__showAnswer--active'
                                            : 'testTSIA2Preview__showAnswer'
                                        }
                                        onClick={() => setShowAnswer(!showAnswer)}
                                    >
                                        {showAnswer ? 'Hide answer' : 'Show answer'}
                                    </button>
                                </div>
                            </div>

                            <div id='testTSIA2Preview__questionChoices' className='testTSIA2Preview__questionChoices'>
                                {question.passages.length === 0 && renderFigures(question, (questionIndex + 1).toString())}
                                {question.instructions &&
                                    <p className='testTSIA2Preview__instructions'>
                                        {question.instructions}
                                    </p>
                                }
                                <p className='testTSIA2Preview__questionQuestion'>
                                    <span className='katex'>
                                        {question.question}
                                    </span>
                                </p>
                                {renderChoices(question, showAnswer)}
                                <div className='testTSIA2Preview__marginBottom'></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='testTSIA2Preview__dottedLine'></div>

                {/* footer */}
                <div className='testTSIA2Preview__footer'>
                    <button
                        className='testTSIA2Preview__navigatorToggle'
                        onClick={() => setNavigatorOpen(!navigatorOpen)}
                    >
                        Question {questionIndex + 1}/{questions.length}
                        {navigatorOpen
                            ? <MdKeyboardArrowDown className='testTSIA2Preview__navigatorToggleIcon' />
                            : <MdKeyboardArrowUp className='testTSIA2Preview__navigatorToggleIcon' />
                        }
                    </button>

                    <div className='testTSIA2Preview__buttons'>
                        <Button
                            className='testTSIA2Preview__button'
                            secondary
                            disabled={moduleIndex === 0 && questionIndex === 0}
                            onClick={prevQuestion}
                        >
                            BACK
                        </Button>
                        <Button
                            className='testTSIA2Preview__button'
                            onClick={nextQuestion}
                        >
                            NEXT
                        </Button>
                    </div>

                    {navigatorOpen &&
                        <div className='testTSIA2Preview__navigator' ref={navigatorRef}>
                            <div className='testTSIA2Preview__navigatorBubble'>
                                <h2 className='testTSIA2Preview__navigatorTitle'>
                                    {name} Questions
                                </h2>
                                <div className='testTSIA2Preview__navigatorLegends'>
                                    <div className='testTSIA2Preview__navigatorLegend'>
                                        <MdOutlineLocationOn className='testTSIA2Preview__navigatorLegendCurrent' />
                                        <p className='testTSIA2Preview__navigatorLegendText'>
                                            Current
                                        </p>
                                    </div>
                                </div>
                                <div className='testTSIA2Preview__navigatorItems'>
                                    {questions.map((item, i) => (
                                        <button
                                            key={`${item.id}nav`}
                                            className='testTSIA2Preview__navigatorItem--correct'
                                            onClick={() => {
                                                goToQuestion(i);
                                                setNavigatorOpen(false);
                                            }}
                                        >
                                            {questionIndex === i
                                                ? <u className='testTSIA2Preview__navigatorItemNumber'>{i + 1}</u>
                                                : <p className='testTSIA2Preview__navigatorItemNumber'>{i + 1}</p>
                                            }
                                            {questionIndex === i && <MdOutlineLocationOn className='testTSIA2Preview__navigatorItemCurrent' />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
        </>
    );
}

export default TestTSIA2Preview;
